import { cloneElement, ReactElement, ReactNode, useMemo } from 'react';

import { Accordion, AccordionButton, AccordionItem, AccordionPanel, Box, Flex, StyleProps } from '@chakra-ui/react';
import { ChevronDownIcon } from '@udacity/chakra-uds-icons';

import { ContentContainer } from '~/components/container';
import { Markdown } from '~/components/markdown';
import { bigSpaceHeight, spaceHeight } from '~/components/space';

export type FAQSectionProps = {
  titleSlot: ReactNode;
  faq: Array<{ _id?: string; question: string; answer: string }>;
  footerSlot?: ReactElement;
};

/**
 *
 * @param footerSlot A _hack_ used on the Plans Page to render the Help Center Info component. ⚠️ Rarely should be used. ⚠️
 * @returns
 */
export function FAQSection({ titleSlot, faq, footerSlot }: FAQSectionProps) {
  const { column1, column2 } = useMemo(() => {
    const midpoint = Math.ceil(faq.length / 2);
    return {
      column1: faq.slice(0, midpoint),
      column2: faq.slice(midpoint),
    };
  }, [faq]);

  const accordianStyleProps: StyleProps = {
    alignContent: 'flex-start',
    display: 'grid',
    flex: '1',
    gap: '22px',
  };

  // Note: this stuff here is super hacky...
  // We need to render this footer slot without affecting the accordian layout. (See Plans Page for example.)
  const { mobileFooterSlot, desktopFooterSlot } = useMemo(() => {
    return {
      mobileFooterSlot: footerSlot
        ? cloneElement(footerSlot, {
            display: { base: 'none', xl: 'block' },
          })
        : null,
      desktopFooterSlot: footerSlot
        ? cloneElement(footerSlot, {
            display: { base: 'block', xl: 'none' },
          })
        : null,
    };
  }, [footerSlot]);

  const renderAccordianItems = (items: typeof faq) =>
    items.map(({ question, answer, _id }) => (
      <AccordionItem
        key={_id || question}
        backgroundColor='unset'
        borderColor='gray.300'
        flex='1 0'
        sx={{
          "& > button[aria-expanded='false']": {
            borderBottom: '1px solid',
            borderColor: 'gray.300',
          },
          "& > button[aria-expanded='true'] + div": {
            borderBottom: '1px solid',
            borderColor: 'gray.300',
          },
        }}
      >
        {({ isExpanded }) => (
          <>
            <AccordionButton backgroundColor='white'>
              <Box as='span' flex='1' textAlign='start'>
                {question}
              </Box>
              <ChevronDownIcon
                height='32px'
                transform={isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}
                transformOrigin='center'
                width='32px'
              />
            </AccordionButton>

            <AccordionPanel backgroundColor='white' pb={4}>
              <Markdown markdownText={answer} />
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    ));

  return (
    <Box bgColor='gray.50'>
      <ContentContainer as='section' paddingBottom={bigSpaceHeight} paddingTop={spaceHeight}>
        {titleSlot}
        <Flex
          gap='32px'
          flexDirection={{
            base: 'column',
            xl: 'row',
          }}
        >
          <Accordion {...accordianStyleProps} allowMultiple>
            {renderAccordianItems(column1)}
            {faq.length % 2 === 0 && mobileFooterSlot}
          </Accordion>
          <Accordion {...accordianStyleProps} allowMultiple>
            {renderAccordianItems(column2)}
            {faq.length % 2 === 1 && mobileFooterSlot}
          </Accordion>
          {desktopFooterSlot}
        </Flex>
      </ContentContainer>
    </Box>
  );
}
